// Augmentation outcome data based on L2 Interlude

// Helper to generate random value in range
const randomInRange = (min, max, decimals = 0) => {
  const value = min + Math.random() * (max - min);
  return decimals > 0 ? value.toFixed(decimals) : Math.round(value);
};

export const STONE_TYPES = {
  LIFE: {
    id: 'life',
    name: 'Life Stone',
    color: '#c9b896',
    skillChance: 0.01,
    icon: '◆'
  },
  MID: {
    id: 'mid',
    name: 'Mid-Grade Life Stone',
    color: '#7a7a80',
    skillChance: 0.06,
    icon: '◆'
  },
  TOP: {
    id: 'top',
    name: 'Top-Grade Life Stone',
    color: '#d4a93c',
    skillChance: 0.25,
    icon: '◆'
  },
  ULTIMATE: {
    id: 'ultimate',
    name: 'Ultimate Life Stone',
    color: '#5b9eb0',
    skillChance: 1.0,
    icon: '★'
  }
};

export const OUTCOME_TYPES = {
  GENERIC: 'generic',
  CORE_STAT: 'core_stat',
  SKILL: 'skill'
};

// Augment stats with paired (2 different stats) and solo (same stat twice) ranges
// Based on interlude-online server settings
export const AUGMENT_STATS = [
  // Defensive stats
  { name: 'P. Def.', paired: { min: 14, max: 30 }, solo: { min: 24, max: 55 } },
  { name: 'M. Def.', paired: { min: 14, max: 30 }, solo: { min: 24, max: 55 } },
  { name: 'Max HP', paired: { min: 90, max: 180 }, solo: { min: 210, max: 340 } },
  { name: 'Max CP', paired: { min: 90, max: 180 }, solo: { min: 210, max: 340 } },
  { name: 'Max MP', paired: { min: 120, max: 220 }, solo: { min: 230, max: 440 } },
  { name: 'Shield Def.', paired: { min: 30, max: 60 }, solo: { min: 80, max: 80 } },
  
  // Offensive stats
  { name: 'P. Atk.', paired: { min: 20, max: 55 }, solo: { min: 60, max: 90 } },
  { name: 'M. Atk.', paired: { min: 20, max: 55 }, solo: { min: 60, max: 90 } },
  { name: 'Critical', paired: { min: 13, max: 18 }, solo: { min: 20, max: 32 } },
  { name: 'Critical Damage', paired: { min: 2, max: 4 }, solo: { min: 5, max: 5 }, suffix: '%' },
  
  // Accuracy/Evasion
  { name: 'Evasion', paired: { min: 1, max: 2 }, solo: { min: 2, max: 4 } },
  { name: 'Accuracy', paired: { min: 1, max: 2 }, solo: { min: 2, max: 4 } },
  
  // Speed stats
  { name: 'Speed', paired: { min: 1, max: 1.8, decimals: 1 }, solo: { min: 2, max: 5 } },
  { name: 'Atk. Spd.', paired: { min: 15, max: 22 }, solo: { min: 25, max: 25 } },
  { name: 'Casting Spd.', paired: { min: 15, max: 18 }, solo: { min: 19, max: 25 } },
  
  // Magic stats
  { name: 'Magic Crit. Rate', paired: { min: 0.5, max: 1.5, decimals: 1 }, solo: { min: 2, max: 2 }, suffix: '%' },
  { name: 'Heal Effect', paired: { min: 5, max: 8 }, solo: { min: 9, max: 9 }, suffix: '%' },
  
  // Misc
  { name: 'Reflect Damage', paired: { min: 2, max: 3 }, solo: { min: 4, max: 4 }, suffix: '%' },
];

// Core stats (rarer)
export const CORE_STATS = [
  { name: 'STR', value: '+1' },
  { name: 'CON', value: '+1' },
  { name: 'INT', value: '+1' },
  { name: 'MEN', value: '+1' },
];

// All 178 Life Stone skills from L2 Interlude
// When skill is obtained, pick 1 random skill from this complete list
export const ALL_SKILLS = [
  { name: 'Active: Hydro Blast', type: 'Active (Magic)', description: 'Summons and hurls powerful water at enemy. Power: 110' },
  { name: 'Passive: Refresh', type: 'Passive', description: 'Decreases re-use time for all skills by 15%' },
  { name: 'Stance: Massive Reflect', type: 'Stance (Magic)', description: 'Reflects 40% of melee damage, max 5500. Not available in Olympiad' },
  { name: 'Chance: Slow', type: 'Chance (5% on hit)', description: 'Instantly reduces target\'s movement speed. Power: 3' },
  { name: 'Chance: Winter', type: 'Chance (5% on hit)', description: 'Instantly reduces target\'s Atk. Spd. Power: 3' },
  { name: 'Chance: Stun', type: 'Chance (5% on hit)', description: 'Instantly stuns target' },
  { name: 'Chance: Hold', type: 'Chance (5% on hit)', description: 'Instantly roots target in place' },
  { name: 'Passive: Skill Mastery', type: 'Passive', description: 'Increases chance to use skills without delay and double duration' },
  { name: 'Chance: Paralyze', type: 'Chance (5% on hit)', description: 'Instantly paralyzes target' },
  { name: 'Passive: Paralyze Resistance', type: 'Passive', description: 'Increases resistance to paralyze' },
  { name: 'Active: Prominence', type: 'Active (Magic)', description: 'Hurls huge lightning ball at enemy. Power: 110' },
  { name: 'Chance: Poison', type: 'Chance (5% on hit)', description: 'Instantly poisons target. Power: 8' },
  { name: 'Chance: Bleed', type: 'Chance (5% on hit)', description: 'Instantly causes bleeding wound. Power: 8' },
  { name: 'Chance: Silence', type: 'Chance (5% on hit)', description: 'Instantly blocks magic skills' },
  { name: 'Chance: Doom', type: 'Chance (5% on hit)', description: 'Instantly blocks all skills' },
  { name: 'Chance: Mana Burn', type: 'Chance (5% on magic)', description: 'Burns target\'s MP. Power: 88' },
  { name: 'Chance: Slow (Magic)', type: 'Chance (5% on magic)', description: 'Reduces movement speed when using magic. Power: 3' },
  { name: 'Chance: Winter (Magic)', type: 'Chance (5% on magic)', description: 'Reduces Atk. Spd when using magic. Power: 3' },
  { name: 'Chance: Stun (Magic)', type: 'Chance (5% on magic)', description: 'Stuns target when using magic' },
  { name: 'Chance: Hold (Magic)', type: 'Chance (5% on magic)', description: 'Roots target when using magic' },
  { name: 'Stance: Revenge', type: 'Stance (Physical)', description: '40% chance to reflect melee damage. Not in Olympiad' },
  { name: 'Chance: Paralyze (Magic)', type: 'Chance (5% on magic)', description: 'Paralyzes target when using magic' },
  { name: 'Stance: Ritual of Pain', type: 'Stance (Physical)', description: 'Restores CP when receiving damage' },
  { name: 'Chance: Fear (Magic)', type: 'Chance (5% on magic)', description: 'Causes fear when using magic' },
  { name: 'Chance: Poison (Magic)', type: 'Chance (5% on magic)', description: 'Poisons target when using magic. Power: 8' },
  { name: 'Chance: Bleed (Magic)', type: 'Chance (5% on magic)', description: 'Causes bleeding when using magic. Power: 8' },
  { name: 'Chance: Silence (Magic)', type: 'Chance (5% on magic)', description: 'Blocks magic when using magic' },
  { name: 'Chance: Doom (Magic)', type: 'Chance (5% on magic)', description: 'Blocks all skills when using magic' },
  { name: 'Passive: Physical Skill Power', type: 'Passive', description: 'Increases P. Skill damage by 9%, M. Skill by 6%' },
  { name: 'Active: Big Bomb', type: 'Active (Physical)', description: 'Creates bomb that explodes after few seconds' },
  { name: 'Chance: Mana Burn (Crit)', type: 'Chance (10% on crit)', description: 'Burns MP on critical hit. Power: 88' },
  { name: 'Chance: Slow (Crit)', type: 'Chance (10% on crit)', description: 'Reduces speed on critical hit. Power: 3' },
  { name: 'Chance: Winter (Crit)', type: 'Chance (10% on crit)', description: 'Reduces Atk. Spd on crit. Power: 3' },
  { name: 'Chance: Stun (Crit)', type: 'Chance (10% on crit)', description: 'Stuns on critical hit' },
  { name: 'Chance: Hold (Crit)', type: 'Chance (10% on crit)', description: 'Roots on critical hit' },
  { name: 'Passive: Potion Sensibility', type: 'Passive', description: 'Increases potion effectiveness' },
  { name: 'Chance: Paralyze (Crit)', type: 'Chance (10% on crit)', description: 'Paralyzes on critical hit' },
  { name: 'Active: Hurricane', type: 'Active (Magic)', description: 'Hurls powerful hurricane at enemy. Power: 110' },
  { name: 'Chance: Poison (Crit)', type: 'Chance (10% on crit)', description: 'Poisons on critical hit. Power: 8' },
  { name: 'Chance: Bleed (Crit)', type: 'Chance (10% on crit)', description: 'Causes bleeding on crit. Power: 8' },
  { name: 'Chance: Silence (Crit)', type: 'Chance (10% on crit)', description: 'Blocks magic on critical hit' },
  { name: 'Chance: Doom (Crit)', type: 'Chance (10% on crit)', description: 'Blocks all skills on crit' },
  { name: 'Active: Heal', type: 'Active (Magic)', description: 'Restores HP. Power: 552' },
  { name: 'Buff: Blessed Body', type: 'Buff (Magic)', description: 'Increases Max HP by 19%' },
  { name: 'Buff: Battle Roar', type: 'Buff (Physical)', description: 'Increases Max HP by 19% and restores HP' },
  { name: 'Buff: Prayer', type: 'Buff (Magic)', description: 'Increases heal effectiveness by 19%' },
  { name: 'Active: Recharge', type: 'Active (Magic)', description: 'Restores MP. Power: 69' },
  { name: 'Buff: Blessed Soul', type: 'Buff (Magic)', description: 'Increases Max MP by 19%' },
  { name: 'Buff: Mana Gain', type: 'Buff (Magic)', description: 'Increases MP regeneration' },
  { name: 'Active: Ritual', type: 'Active (Magic)', description: 'Restores CP. Power: 473' },
  { name: 'Buff: Cheer', type: 'Buff (Magic)', description: 'Increases Max CP by 19%' },
  { name: 'Buff: Might', type: 'Buff (Magic)', description: 'Increases P. Atk' },
  { name: 'Buff: Empower', type: 'Buff (Magic)', description: 'Increases M. Atk' },
  { name: 'Buff: Duel Might', type: 'Buff (Magic)', description: 'Increases P. Atk in PVP' },
  { name: 'Buff: Shield', type: 'Buff (Magic)', description: 'Increases P. Def' },
  { name: 'Buff: Magic Barrier', type: 'Buff (Magic)', description: 'Increases M. Def' },
  { name: 'Active: Duel Weakness', type: 'Active (Magic)', description: 'Decreases enemy P. Atk in PVP' },
  { name: 'Buff: Heal Empower', type: 'Buff (Magic)', description: 'Increases healing power' },
  { name: 'Buff: Agility', type: 'Buff (Magic)', description: 'Increases Evasion' },
  { name: 'Buff: Guidance', type: 'Buff (Magic)', description: 'Increases Accuracy' },
  { name: 'Buff: Focus', type: 'Buff (Magic)', description: 'Increases Critical Rate' },
  { name: 'Buff: Wild Magic', type: 'Buff (Magic)', description: 'Increases Magic Critical' },
  { name: 'Stance: Defence Concentration', type: 'Stance (Physical)', description: 'Increases defense, reduces attack' },
  { name: 'Active: Jump', type: 'Active (Magic)', description: 'Jump to target' },
  { name: 'Buff: Iron Body', type: 'Buff (Magic)', description: 'Increases P. Def and M. Def by 9%' },
  { name: 'Active: Break Duress', type: 'Active (Physical)', description: 'Remove root' },
  { name: 'Buff: Vampiric Rage', type: 'Buff (Physical)', description: 'Absorbs HP from damage dealt' },
  { name: 'Active: Aggression', type: 'Active (Physical)', description: 'Forces target to attack. Power: 659' },
  { name: 'Active: Charm', type: 'Active (Magic)', description: 'Attracts target' },
  { name: 'Buff: Haste', type: 'Buff (Physical)', description: 'Increases Atk. Spd by 5%. Not in Olympiad' },
  { name: 'Active: Trick', type: 'Active (Physical)', description: 'Removes target buffs' },
  { name: 'Active: Vampiric Touch', type: 'Active (Magic)', description: 'Absorbs HP. Power: 105' },
  { name: 'Active: Mana Burn', type: 'Active (Magic)', description: 'Burns enemy MP. Power: 120' },
  { name: 'Active: Escape', type: 'Active (Magic)', description: 'Jump away from target' },
  { name: 'Active: Firecracker', type: 'Active (Physical)', description: 'Launches firecracker' },
  { name: 'Active: Large Firecracker', type: 'Active (Physical)', description: 'Launches large firecracker' },
  { name: 'Active: Celestial Shield', type: 'Buff (Physical)', description: 'Temporary invincibility' },
  { name: 'Active: Stealth', type: 'Buff (Physical)', description: 'Hide from aggressive monsters, stats reduced' },
  { name: 'Active: Resurrection', type: 'Active (Magic)', description: 'Resurrects dead player, 70% exp recovery' },
  { name: 'Buff: Skill Clarity', type: 'Buff (Magic)', description: 'Reduces MP cost for physical skills' },
  { name: 'Buff: Spell Clarity', type: 'Buff (Magic)', description: 'Reduces MP cost for magic skills' },
  { name: 'Active: Braveheart', type: 'Active (Physical)', description: 'Restores CP. Power: 1000' },
  { name: 'Buff: Clarity', type: 'Buff (Magic)', description: 'Reduces MP cost for all skills' },
  { name: 'Active: Stone', type: 'Active (Magic)', description: 'Hurls stone boulders at enemy. Power: 110' },
  { name: 'Active: Solar Flare', type: 'Active (Magic)', description: 'Holy light attack. Power: 110' },
  { name: 'Active: Shadow Flare', type: 'Active (Magic)', description: 'Dark attack. Power: 110' },
  { name: 'Active: Aura Flare', type: 'Active (Magic)', description: 'Elemental magic attack. Power: 99' },
  { name: 'Area: Prominence', type: 'Active AoE (Magic)', description: 'Fireball on enemies around target. Power: 55' },
  { name: 'Area: Hydro Blast', type: 'Active AoE (Magic)', description: 'Water on enemies around target. Power: 55' },
  { name: 'Area: Hurricane', type: 'Active AoE (Magic)', description: 'Hurricane on enemies around target. Power: 55' },
  { name: 'Area: Stone', type: 'Active AoE (Magic)', description: 'Stones on enemies around target. Power: 55' },
  { name: 'Area: Solar Flare', type: 'Active AoE (Magic)', description: 'Holy light on enemies around target. Power: 55' },
  { name: 'Area: Shadow Flare', type: 'Active AoE (Magic)', description: 'Darkness on enemies around target. Power: 55' },
  { name: 'Area: Aura Flare', type: 'Active AoE (Magic)', description: 'Elemental on enemies around target. Power: 55' },
  { name: 'Aura: Prominence', type: 'Active Aura (Magic)', description: 'Fireball on nearby enemies. Power: 55' },
  { name: 'Aura: Hydro Blast', type: 'Active Aura (Magic)', description: 'Water on nearby enemies. Power: 55' },
  { name: 'Aura: Hurricane', type: 'Active Aura (Magic)', description: 'Hurricane on nearby enemies. Power: 55' },
  { name: 'Aura: Stone', type: 'Active Aura (Magic)', description: 'Stones on nearby enemies. Power: 55' },
  { name: 'Aura: Solar Flare', type: 'Active Aura (Magic)', description: 'Holy light on nearby enemies. Power: 55' },
  { name: 'Aura: Shadow Flare', type: 'Active Aura (Magic)', description: 'Darkness on nearby enemies. Power: 55' },
  { name: 'Aura: Aura Flare', type: 'Active Aura (Magic)', description: 'Elemental on nearby enemies. Power: 55' },
  { name: 'Active: Slow', type: 'Active (Magic)', description: 'Reduces movement speed' },
  { name: 'Aura: Winter', type: 'Active Aura (Magic)', description: 'Reduces Atk. Spd' },
  { name: 'Active: Stun', type: 'Active (Physical)', description: 'Stuns target' },
  { name: 'Active: Hold', type: 'Active (Magic)', description: 'Roots target' },
  { name: 'Active: Sleep', type: 'Active (Magic)', description: 'Puts target to sleep' },
  { name: 'Active: Paralyze', type: 'Active (Magic)', description: 'Paralyzes target' },
  { name: 'Active: Medusa', type: 'Active (Physical)', description: 'Petrifies target' },
  { name: 'Active: Fear', type: 'Active (Magic)', description: 'Causes fear and panic' },
  { name: 'Active: Poison', type: 'Active (Magic)', description: 'Poisons target. Power: 8' },
  { name: 'Active: Bleed', type: 'Active (Physical)', description: 'Causes bleeding. Power: 8' },
  { name: 'Active: Silence', type: 'Active (Magic)', description: 'Blocks magic skills' },
  { name: 'Active: Doom', type: 'Active (Magic)', description: 'Blocks all skills' },
  { name: 'Buff: Skill Refresh', type: 'Buff (Physical)', description: 'Reduces physical skill reuse time' },
  { name: 'Buff: Spell Refresh', type: 'Buff (Physical)', description: 'Reduces magic skill reuse time' },
  { name: 'Toggle: Fake Death', type: 'Toggle', description: 'Pretend death. Consumes MP constantly' },
  { name: 'Buff: Refresh', type: 'Buff (Physical)', description: 'Reduces reuse time for all skills' },
  { name: 'Buff: Problem Skill', type: 'Buff (Physical)', description: 'Increases head size' },
  { name: 'Buff: Reflect Damage', type: 'Buff (Magic)', description: 'Reflects physical damage except skills and ranged' },
  { name: 'Active: Shadow Walk', type: 'Buff (Physical)', description: 'Invisible movement, walking only' },
  { name: 'Active: Self Medusa', type: 'Buff (Physical)', description: 'Turn self to stone' },
  { name: 'Chance: Heal', type: 'Chance (5% on hit)', description: 'Restores HP on attack' },
  { name: 'Chance: Blessed Body', type: 'Chance (5% on hit)', description: 'Increases Max HP on attack' },
  { name: 'Stance: Quick Step', type: 'Stance (Physical)', description: 'Increases run speed by 10. Not in Olympiad' },
  { name: 'Chance: Recharge', type: 'Chance (5% on skill)', description: 'Restores MP when using skills' },
  { name: 'Chance: Blessed Soul', type: 'Chance (5% when attacked)', description: 'Increases Max MP when attacked' },
  { name: 'Passive: Archery', type: 'Passive', description: 'Increases bow range by 80' },
  { name: 'Chance: Ritual', type: 'Chance (5% when attacked)', description: 'Restores CP when attacked' },
  { name: 'Stance: Mystic Immunity', type: 'Stance (Magic)', description: 'Immune to buffs/debuffs. Not in Olympiad' },
  { name: 'Stance: Might', type: 'Stance (Physical)', description: 'Increases P. Atk' },
  { name: 'Stance: Empower', type: 'Stance (Magic)', description: 'Increases M. Atk' },
  { name: 'Stance: Duel Might', type: 'Stance (Physical)', description: 'Increases P. Atk in PVP' },
  { name: 'Stance: Shield', type: 'Stance (Physical)', description: 'Increases P. Def' },
  { name: 'Stance: Magic Barrier', type: 'Stance (Magic)', description: 'Increases M. Def' },
  { name: 'Chance: Duel Weakness', type: 'Chance (5% when attacked)', description: 'Reduces enemy P. Atk in PVP when attacked' },
  { name: 'Stance: Agility', type: 'Stance (Physical)', description: 'Increases Evasion' },
  { name: 'Stance: Guidance', type: 'Stance (Physical)', description: 'Increases Accuracy' },
  { name: 'Stance: Focus', type: 'Stance (Physical)', description: 'Increases Critical Rate' },
  { name: 'Stance: Wild Magic', type: 'Stance (Magic)', description: 'Increases Magic Critical' },
  { name: 'Chance: Charm', type: 'Chance (5% when attacked)', description: 'Reduces aggression when attacked' },
  { name: 'Chance: Slow (Attacked)', type: 'Chance (5% when attacked)', description: 'Reduces speed when attacked' },
  { name: 'Chance: Winter (Attacked)', type: 'Chance (5% when attacked)', description: 'Reduces Atk. Spd when attacked' },
  { name: 'Chance: Stun (Attacked)', type: 'Chance (5% when attacked)', description: 'Stuns when attacked' },
  { name: 'Chance: Hold (Attacked)', type: 'Chance (5% when attacked)', description: 'Roots when attacked' },
  { name: 'Chance: Sleep (Attacked)', type: 'Chance (5% when attacked)', description: 'Sleeps when attacked' },
  { name: 'Chance: Paralyze (Attacked)', type: 'Chance (5% when attacked)', description: 'Paralyzes when attacked' },
  { name: 'Chance: Medusa (Attacked)', type: 'Chance (5% when attacked)', description: 'Petrifies when attacked' },
  { name: 'Chance: Fear (Attacked)', type: 'Chance (5% when attacked)', description: 'Causes fear when attacked' },
  { name: 'Chance: Poison (Attacked)', type: 'Chance (5% when attacked)', description: 'Poisons when attacked. Power: 8' },
  { name: 'Chance: Bleed (Attacked)', type: 'Chance (5% when attacked)', description: 'Bleeds when attacked. Power: 8' },
  { name: 'Chance: Silence (Attacked)', type: 'Chance (5% when attacked)', description: 'Silences when attacked' },
  { name: 'Chance: Doom (Attacked)', type: 'Chance (5% when attacked)', description: 'Dooms when attacked' },
  { name: 'Passive: Prayer', type: 'Passive', description: 'Increases healing effectiveness' },
  { name: 'Passive: Mana Gain', type: 'Passive', description: 'Increases MP regeneration' },
  { name: 'Passive: Might', type: 'Passive', description: 'Increases P. Atk' },
  { name: 'Passive: Empower', type: 'Passive', description: 'Increases M. Atk' },
  { name: 'Passive: Duel Might', type: 'Passive', description: 'Increases P. Atk in PVP' },
  { name: 'Passive: Shield', type: 'Passive', description: 'Increases P. Def' },
  { name: 'Passive: Magic Barrier', type: 'Passive', description: 'Increases M. Def' },
  { name: 'Passive: Heal Empower', type: 'Passive', description: 'Increases healing power' },
  { name: 'Passive: Agility', type: 'Passive', description: 'Increases Evasion' },
  { name: 'Passive: Guidance', type: 'Passive', description: 'Increases Accuracy' },
  { name: 'Passive: Focus', type: 'Passive', description: 'Increases Critical Rate' },
  { name: 'Passive: Wild Magic', type: 'Passive', description: 'Increases Magic Critical' },
  { name: 'Passive: Skill Clarity', type: 'Passive', description: 'Reduces MP cost for physical skills' },
  { name: 'Passive: Spell Clarity', type: 'Passive', description: 'Reduces MP cost for magic skills' },
  { name: 'Passive: Clarity', type: 'Passive', description: 'Reduces MP cost for all skills' },
  { name: 'Passive: Reflect Damage', type: 'Passive', description: 'Reflects physical damage except skills and ranged' }
];

// Generate a random stat value from a range definition
function generateStatValue(range) {
  const decimals = range.decimals || 0;
  return randomInRange(range.min, range.max, decimals);
}

// Format stat value with proper prefix and suffix
function formatStatValue(value, suffix = '') {
  return `+${value}${suffix}`;
}

// Generate stats for augmentation outcome
// If both stat picks are the same -> solo stat (higher range)
// If different stats -> paired stats (lower range each)
function generateAugmentStats() {
  // Pick 2 random stat indices
  const statIndex1 = Math.floor(Math.random() * AUGMENT_STATS.length);
  const statIndex2 = Math.floor(Math.random() * AUGMENT_STATS.length);
  
  const stat1 = AUGMENT_STATS[statIndex1];
  const stat2 = AUGMENT_STATS[statIndex2];
  
  if (statIndex1 === statIndex2) {
    // Same stat twice -> use solo range
    const value = generateStatValue(stat1.solo);
    return [{
      name: stat1.name,
      value: formatStatValue(value, stat1.suffix || '')
    }];
  } else {
    // Different stats -> use paired ranges
    const value1 = generateStatValue(stat1.paired);
    const value2 = generateStatValue(stat2.paired);
    return [
      { name: stat1.name, value: formatStatValue(value1, stat1.suffix || '') },
      { name: stat2.name, value: formatStatValue(value2, stat2.suffix || '') }
    ];
  }
}

// Get random outcome based on stone type
export function getRandomOutcome(stoneType) {
  const stone = STONE_TYPES[stoneType];
  const roll = Math.random();
  
  // Determine if skill is acquired
  if (roll < stone.skillChance) {
    // Skill! Pick 1 random skill from all 178 L2 Interlude skills
    const skill = ALL_SKILLS[Math.floor(Math.random() * ALL_SKILLS.length)];
    
    // Also get stats with skill
    const stats = generateAugmentStats();
    
    return {
      type: OUTCOME_TYPES.SKILL,
      skill,
      stats,
      rarity: 'legendary'
    };
  } else {
    // No skill - just stats
    const statRoll = Math.random();
    
    if (statRoll < 0.15) {
      // Core stat (15% of non-skill outcomes)
      const coreStat = CORE_STATS[Math.floor(Math.random() * CORE_STATS.length)];
      return {
        type: OUTCOME_TYPES.CORE_STAT,
        stats: [{ name: coreStat.name, value: '+1' }],
        skill: null,
        rarity: 'rare'
      };
    } else {
      // Generic stats using new paired/solo system
      const stats = generateAugmentStats();
      
      return {
        type: OUTCOME_TYPES.GENERIC,
        stats,
        skill: null,
        rarity: 'common'
      };
    }
  }
}
